import { CreditCard } from 'lucide-react'
import * as React from 'react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AutopayConsentChoice, type AutopayOffer } from '@/components/AutopayConsentChoice'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { stripePromise } from '@/lib/stripe'
import { displayPortalDate } from './memberPortalPresentation'

interface PortalAutopay {
  status: 'active' | 'inactive'
  card: { brand: string; last4: string; expiry: string } | null
  card_retained_reason: string | null
  next_charge: { amount: string; amount_cents: number; charge_on: string } | null
  offer: {
    eligible: boolean
    reason: string | null
    default_checked: false
    disclosure: AutopayOffer | null
  } | null
}

interface PortalAutopayResponse {
  autopay: PortalAutopay
}

interface CardSetupResponse {
  card_setup: { id: number; client_secret: string }
}

const queryKey = ['member-portal-autopay'] as const

export function MemberPortalAutopay() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const query = useQuery({
    queryKey,
    queryFn: () => getJSON<PortalAutopayResponse>('/api/v1/member_portal/autopay'),
    retry: false,
  })
  const turnOff = useMutation({
    mutationFn: () => deleteJSON<PortalAutopayResponse>('/api/v1/member_portal/autopay'),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
  })
  const autopay = query.data?.autopay

  if (query.isLoading) {
    return (
      <section className="mt-6 space-y-3" aria-labelledby="portal-autopay-heading">
        <Heading />
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading Autopay...
        </p>
      </section>
    )
  }
  if (query.isError || !autopay) return <Alert className="mt-6">Autopay unavailable</Alert>

  const saveComplete = (data: PortalAutopayResponse) => {
    queryClient.setQueryData(queryKey, data)
    setEditing(false)
  }

  return (
    <section className="mt-6 space-y-3" aria-labelledby="portal-autopay-heading">
      <Heading />
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          {autopay.status === 'active' && autopay.card ? (
            <CardSummary autopay={autopay} />
          ) : (
            <div>
              <p className="text-caption text-muted-foreground">Autopay is off</p>
              {autopay.card_retained_reason && (
                <p className="mt-1 text-caption text-muted-foreground">
                  {autopay.card_retained_reason}
                </p>
              )}
            </div>
          )}
          <div className="flex shrink-0 items-center gap-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              {autopay.status === 'active' ? 'Update card' : 'Set up Autopay'}
            </Button>
            {autopay.status === 'active' && (
              <TurnOffDialog pending={turnOff.isPending} onConfirm={() => turnOff.mutate()} />
            )}
          </div>
        </div>
        {editing && (
          <div className="mt-4 border-t border-border pt-4">
            <CardSetup
              enroll={autopay.status !== 'active'}
              offer={autopay.offer?.disclosure ?? null}
              onSaved={saveComplete}
              onCancel={() => setEditing(false)}
            />
          </div>
        )}
        {turnOff.isError && <Alert className="mt-4">Autopay could not be turned off.</Alert>}
      </Card>
    </section>
  )
}

function Heading() {
  return (
    <h2 id="portal-autopay-heading" className="text-body font-semibold text-foreground">
      Autopay
    </h2>
  )
}

function CardSummary({ autopay }: { autopay: PortalAutopay }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
        <CreditCard className="size-4" aria-hidden="true" />
      </div>
      <div>
        <p className="text-body-sm font-medium">
          {autopay.card?.brand} •••• {autopay.card?.last4}
        </p>
        {autopay.next_charge && (
          <p className="text-caption text-muted-foreground">
            Next charge {autopay.next_charge.amount} on{' '}
            {displayPortalDate(autopay.next_charge.charge_on)}
          </p>
        )}
      </div>
    </div>
  )
}

function CardSetup({
  enroll,
  offer,
  onSaved,
  onCancel,
}: {
  enroll: boolean
  offer: AutopayOffer | null
  onSaved: (data: PortalAutopayResponse) => void
  onCancel: () => void
}) {
  if (!stripePromise) return <Alert>Saved Card entry is not configured.</Alert>

  return (
    <Elements stripe={stripePromise}>
      <HostedCardSetup enroll={enroll} offer={offer} onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  )
}

function HostedCardSetup({
  enroll,
  offer,
  onSaved,
  onCancel,
}: {
  enroll: boolean
  offer: AutopayOffer | null
  onSaved: (data: PortalAutopayResponse) => void
  onCancel: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [consent, setConsent] = React.useState(false)
  const idempotencyKey = React.useRef(crypto.randomUUID())
  const save = useMutation({
    mutationFn: async () => {
      const card = elements?.getElement(CardElement)
      if (!stripe || !card) throw new Error('Processor-hosted card entry is not ready.')
      const setup = await postJSON<CardSetupResponse>('/api/v1/member_portal/autopay/card_setups', {
        card_setup: { idempotency_key: idempotencyKey.current, consent },
      })
      const confirmation = await stripe.confirmCardSetup(setup.card_setup.client_secret, {
        payment_method: { card },
      })
      if (confirmation.error) throw new Error(confirmation.error.message ?? 'Saved Card failed.')
      return postJSON<PortalAutopayResponse>(
        `/api/v1/member_portal/autopay/card_setups/${setup.card_setup.id}/complete`,
        {},
      )
    },
    onSuccess: onSaved,
  })

  return (
    <div className="space-y-3">
      {enroll && offer && (
        <AutopayConsentChoice offer={offer} checked={consent} onCheckedChange={setConsent} />
      )}
      <div className="space-y-1.5">
        <p className="text-body-sm font-medium">Card details</p>
        <div className="rounded-md border border-border bg-background px-3 py-3">
          <CardElement options={{ hidePostalCode: false }} />
        </div>
        <p className="text-caption text-muted-foreground">
          Card details are securely collected by Stripe. Saving a card does not take a payment.
        </p>
      </div>
      {save.isError && <Alert variant="destructive">{save.error.message}</Alert>}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!stripe || save.isPending || (enroll && !consent)}
          onClick={() => save.mutate()}
        >
          {save.isPending
            ? 'Saving card...'
            : enroll
              ? 'Save card and enroll'
              : 'Update Saved Card'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function TurnOffDialog({ pending, onConfirm }: { pending: boolean; onConfirm: () => void }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="shadow-none">
          Turn off
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Turn off Autopay?</DialogTitle>
          <DialogDescription>
            This ends Autopay and detaches your Saved Card. Your Membership is unchanged.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" size="sm">
              Keep Autopay
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={pending}
              onClick={onConfirm}
            >
              {pending ? 'Turning off...' : 'Turn off Autopay'}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
